import classNames from "classnames";
import { GraphQLError } from "graphql";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import NumericInput from "react-numeric-input";
import { useSearchParams } from "react-router-dom";
import {
  doFastCheckinMutation,
  doFastCheckoutMutation,
  redirectPaymentGatewayForAmountMutation,
  redirectPaymentGatewayForInstalmentMutation,
  resetCartMutation,
  unlockBookingMutation,
  updateCustomerBookingMutation,
} from "rsl-api-106/lib/mutations";
import {
  getCustomerBookingsQuery,
  getPaymentPlansQuery,
} from "rsl-api-106/lib/queries";
import {
  BookingStatus,
  CustomerBooking,
  Mutation,
  MutationDoFastCheckinArgs,
  MutationDoFastCheckoutArgs,
  MutationRedirectPaymentGatewayForAmountArgs,
  MutationRedirectPaymentGatewayForInstalmentArgs,
  MutationResetCartArgs,
  MutationUnlockBookingArgs,
  MutationUpdateCustomerBookingArgs,
  Occupant,
  PaymentGatewayForAmountInput,
  PaymentGatewayForInstalmentInput,
  Query,
  QueryGetCustomerBookingsArgs,
  QueryGetPaymentPlansArgs,
} from "rsl-api-106/lib/types";

import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import Popup from "@components/_Layout/Controls/Popup";
import SelectPaymentMethods from "@components/_Layout/Controls/SelectPaymentMethods";
import Disclosure from "@components/_Layout/Disclosure";
import EmptyState from "@components/_Layout/EmptyState";
import LoadingDots from "@components/_Layout/Loaders/LoadingDots";
import LoadingSpinner from "@components/_Layout/Loaders/LoadingSpinner";
import PropertyPopup from "@components/Property/PropertyPopup";
import ReservationDocuments from "@components/Reservation/ReservationDocuments";
import { useCart, useConfig, useSession } from "@hooks";
import { ArrowCircleRight } from "@mui/icons-material";
import {
  doFastCheckinLightMutation,
  getCustomerBookingsWithoutCriteriasQuery,
  MutationUnlockBookingAndDoFastcheckinArgs,
  unlockBookingAndDoFastcheckinMutation,
} from "@services/graphql2/querymutations";
import { getCurrencySymbol } from "@utils/currencies";
import dateFormat from "@utils/dateformat";
import { replaceImgOnError } from "@utils/dom";
import { getLocalStorageItem } from "@utils/localStorage";
import { isReservationPaid } from "@utils/tools";

import { PspCallbackStatus } from "../../../types";

const languagesVarsCodes = [
  "LANG_neobooking_psp_success", //Votre paiement a été validée
  "LANG_neobooking_psp_decline", //Votre paiement a été décliné
  "LANG_neobooking_psp_cancel", //Votre paiement a été annulé.
  "LANG_neobooking_psp_exception", //Une erreur est survenue lors de votre paiement.
  "LANG_neobooking_add_reservation", //Ajouter une nouvelle réservation
  "LANG_neobooking_filter_by_etabs", //Filtrer par établissement
  "LANG_neobooking_res_table_num", //N° rés.
  "LANG_neobooking_res_table_name", //Nom
  "LANG_neobooking_res_table_dates", //Dates
  "LANG_neobooking_res_table_status", //Statut
  "LANG_neobooking_res_table_by", //Souscrite par
  "LANG_neobooking_res_table_amount", //Montant
  "LANG_neobooking_area_res_current_reservation", //Votre réservation en cours
  "LANG_neobooking_area_res_passed_reservation", //Vos réservations passées
  "LANG_neobooking_area_res_upcoming_reservation", //Vos réservations à venir
  "LANG_neobooking_reservations_empty", //Votre liste de réservations est vide
  "LANG_neobooking_reservations_empty_description", //Consultez et modifiez les détails de vos réservations ici.
  "LANG_neobooking_invalid_amount_input", //Le montant saisie n'est pas valide.
  "LANG_neobooking_area_res_update_stay", //Modifier le séjour
  "LANG_neobooking_area_res_updating_stay_in_progress", //Modifier le séjour…
  "LANG_neobooking_area_res_edit_primary_contact_data", //Éditer les coordonnées du contact principal
  "LANG_neobooking_area_res_edit_primary_contact", //Éditer le contact principal
  "LANG_neobooking_area_res_do_fastcheckin", //	Effectuer le pré check-in
  "LANG_neobooking_area_res_doing_fastcheckin", //Effectuer le pré check-in…"
  "LANG_neobooking_area_res_do_fastcheckout", //	Effectuer le pré check-out
  "LANG_neobooking_area_res_doing_fastcheckout", //Effectuer le pré check-out…"
  "LANG_neobooking_area_res_cancel", //Cancel
  "LANG_neobooking_area_res_documents", //Documents
  "LANG_neobooking_area_res_edit_documents", //Edition & documents
  "LANG_neobooking_area_res_documents_popup", //Réservation documents
  "LANG_neobooking_area_res_fastcheckin_primary_contact", //Votre pré check-in - Confirmation du contact principal
  "LANG_neobooking_area_res_payment_pay", //Régler
  "LANG_neobooking_area_res_payment_pay_custom", //Ou payer un montant sur la reservation
  "LANG_neobooking_area_res_payment_pay_amount", //Régler un montant
  "LANG_neobooking_area_res_payment_redirect_in_progress", //Redirection en cours...
  "LANG_neobooking_area_res_payment_amount_error", //*Le montant doit être compris entre 0 et
  "LANG_neobooking_area_res_payment_due_popup", //Échéances des paiements
  "LANG_neobooking_area_res_payment", //Paiements
  "LANG_neobooking_area_res_payment_due_by", //Échéance due par
  "LANG_neobooking_area_res_payment_due_date", //Date d'échéance
  "LANG_neobooking_area_res_payment_due_date_pay", //Date du règlement
  "LANG_neobooking_area_res_payment_to_pay", //	à régler
  "LANG_neobooking_area_res_payment_amount_paid", //Montant réglé
  "LANG_neobooking_area_res_payment_amount_due", //	Montant restant dû
  "LANG_neobooking_area_res_payment_action", //Action
  "LANG_neobooking_from_date", //Du
  "LANG_neobooking_to_date", //Au
  "LANG_neobooking_nights", //Nuits
  "LANG_neobooking_res_table_num_long", //Réservation N°
  "LANG_neobooking_area_res_contact_etab", //Contacter l'établissement
  "LANG_neobooking_area_res_my_documents", //Mes documents
  "LANG_neobooking_area_res_pay_stay", //Régler le séjour
  "LANG_neobooking_edit", //Modifier
  "LANG_neobooking_editing", //Modification
];

type ReservationsAreaProps = {
  //
};

/**
 * Given a reservation, return the name of the rooms that are part of the reservation
 * @param {Reservation} reservation - Reservation
 * @returns The name of the rooms that are being reserved.
 */
const getReservationRoomsInfosName = (reservation: CustomerBooking) => {
  let title = "";
  reservation?.stays?.forEach((stay, stayIndex) => {
    return (
      <div>
        {" "}
        {stay?.distribution?.roomTypes?.forEach((roomType) => {
          title =
            title +
            `${stayIndex === 0 ? "" : "+ "} ${roomType?.quantity}x ${
              roomType?.label ?? ""
            } `;
        })}
      </div>
    );
  });

  return title;
};

const ReservationsArea: React.FC<ReservationsAreaProps> = () => {
  /* Hooks */
  const { sessionId, getLanguageVarValue, authMode } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useQuery<Query, QueryGetCustomerBookingsArgs>(
    getCustomerBookingsWithoutCriteriasQuery(),
    {
      variables: { session: { name: sessionId!! }, input: { option: true } },
      fetchPolicy: "no-cache",
    }
  );

  /* Reservations State */
  const [customerReservations, setCustomerReservation] =
    useState<CustomerBooking[] | null>(null);
  const [currentReservation, setCurrentReservation] =
    useState<CustomerBooking | null>(null);

  /* PSP Callback State */
  const [pspResponseMessage, setPspResponseMessage] =
    useState<{ code: PspCallbackStatus; message: string } | null>(null);

  /* Grouping the reservations by etab id and then creating a select option for each etab. */
  useEffect(() => {
    if (data && data.getCustomerBookings?.customerBookings) {
      setCustomerReservation(
        data.getCustomerBookings?.customerBookings as CustomerBooking[]
      );
      handleSetCurrentReservation(
        data.getCustomerBookings?.customerBookings as CustomerBooking[]
      );
    }
  }, [data]);

  /* Get the PSP Status searchParams & display Alert */
  useEffect(() => {
    const pspStatus = searchParams.get("s");
    const reservationId = searchParams.get("r");
    if (pspStatus) {
      //const concernant = reservationId ? ` concernant la réservation #${reservationId} ` : " "
      switch (pspStatus as PspCallbackStatus) {
        case "s":
          setPspResponseMessage({
            code: pspStatus as PspCallbackStatus,
            message: getLanguageVarValue("LANG_neobooking_psp_success"),
          });
          break;
        case "d":
          setPspResponseMessage({
            code: pspStatus as PspCallbackStatus,
            message: getLanguageVarValue("LANG_neobooking_psp_decline"),
          });
          break;
        case "c":
          setPspResponseMessage({
            code: pspStatus as PspCallbackStatus,
            message: getLanguageVarValue("LANG_neobooking_psp_cancel"),
          });
          break;
        case "e":
          setPspResponseMessage({
            code: pspStatus as PspCallbackStatus,
            message: getLanguageVarValue("LANG_neobooking_psp_exception"),
          });
          break;
      }
    }
  }, []);

  const handleSetCurrentReservation = (resas: CustomerBooking[]) => {
    const r = handleGetClosestDate(resas);
    setCurrentReservation(r);
  };

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handleGetClosestDate = (
    resas: CustomerBooking[]
  ): CustomerBooking | null => {
    let closest: CustomerBooking | null = null;
    let closestDiff = Number.MAX_VALUE;
    const today = new Date();
    [...resas].forEach((reservation) => {
      const startDiff = Math.abs(
        new Date(reservation.startDate).getTime() - today.getTime()
      );
      const endDiff = Math.abs(
        new Date(reservation.endDate).getTime() - today.getTime()
      );
      const minDiff = Math.min(startDiff, endDiff);
      if (minDiff < closestDiff && new Date(reservation.startDate) >= today) {
        closest = reservation;
        closestDiff = minDiff;
      }
    });
    return closest;
  };

  const getRedirectionURL = () => {
    const webuser = getWebUser();
    if (webuser === "webEN" || webuser === "web_en_b2b") {
      return `${
        process.env.REACT_APP_CUSTOMER_URL_EN ?? window.location.origin
      }?webuser=${webuser}&session=${sessionId}`;
    } else {
      return `${
        process.env.REACT_APP_CUSTOMER_URL ?? window.location.origin
      }?webuser=${webuser}&session=${sessionId}`;
    }
  };

  return (
    <div>
      {pspResponseMessage && (
        <div className="tw-my-6">
          <Alert
            variant={
              pspResponseMessage.code === "c"
                ? "danger"
                : pspResponseMessage.code === "d"
                ? "warning"
                : pspResponseMessage.code === "e"
                ? "warning"
                : "primary"
            }
          >
            {pspResponseMessage.message}
          </Alert>
        </div>
      )}
      {authMode === 2 ? (
        <div className="tw-flex tw-items-end tw-justify-end">
          <a
            href={getRedirectionURL()}
            className="tw-rounded-full tw-uppercase tw-cursor-pointer tw-text-v_blue-dark tw-border hover:tw-font-semibold tw-text-sm tw-px-2 tw-py-1 tw-border-v_blue-dark"
          >
            {getLanguageVarValue("LANG_neobooking_add_reservation")}
          </a>
        </div>
      ) : (
        <></>
      )}
      <div className="tw-my-6">
        {!loading && customerReservations && customerReservations.length > 0 ? (
          <>
            {currentReservation && (
              <div className="tw-mb-8">
                <div className="tw-font-bold lg:tw-text-lg tw-mb-4">
                  {getLanguageVarValue(
                    "LANG_neobooking_area_res_current_reservation"
                  )}
                </div>
                <CustomerReservationsItem
                  showPay
                  showUpdate
                  reservation={currentReservation}
                />
              </div>
            )}
            {customerReservations?.filter(
              (r) =>
                r.id !== currentReservation?.id &&
                !r.isCanceled &&
                new Date(r.startDate).getTime() > new Date().getTime()
            ).length > 0 && (
              <div>
                <Disclosure
                  showFleche
                  defaultOpen={true}
                  variant="primary"
                  title={getLanguageVarValue(
                    customerReservations?.filter(
                      (r) =>
                        r.id !== currentReservation?.id &&
                        !r.isCanceled &&
                        new Date(r.startDate).getTime() > new Date().getTime()
                    ).length === 0
                      ? "LANG_neobooking_area_res_next_single_reservation"
                      : "LANG_neobooking_area_res_next_reservation"
                  )}
                >
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-mt-4">
                    {customerReservations
                      ?.filter(
                        (r) =>
                          r.id !== currentReservation?.id &&
                          !r.isCanceled &&
                          new Date(r.startDate).getTime() > new Date().getTime()
                      )
                      .map((reservation, index) => {
                        return (
                          <CustomerReservationsItem
                            key={`CustomerReservationsItem_${reservation.id}`}
                            showPay
                            showUpdate
                            reservation={reservation}
                          />
                        );
                      })}
                  </div>
                </Disclosure>
              </div>
            )}
            {customerReservations?.filter(
              (r) =>
                r.id !== currentReservation?.id &&
                !r.isCanceled &&
                new Date(r.startDate).getTime() < new Date().getTime()
            ).length > 0 && (
              <div className="tw-my-8">
                <Disclosure
                  showFleche
                  variant="primary"
                  title={getLanguageVarValue(
                    "LANG_neobooking_area_res_passed_reservation"
                  )}
                >
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-mt-4">
                    {customerReservations
                      ?.filter(
                        (r) =>
                          r.id !== currentReservation?.id &&
                          !r.isCanceled &&
                          new Date(r.startDate).getTime() < new Date().getTime()
                      )
                      .map((reservation, index) => {
                        return (
                          <CustomerReservationsItem
                            showPay={false}
                            showUpdate={false}
                            reservation={reservation}
                          />
                        );
                      })}
                  </div>
                </Disclosure>
              </div>
            )}
          </>
        ) : loading ? (
          <LoadingSpinner />
        ) : (
          <EmptyState
            title={getLanguageVarValue("LANG_neobooking_reservations_empty")}
            img={
              <svg
                className="tw-mx-auto tw-max-w-lg tw-w-full tw-h-full"
                viewBox="0 0 900 600"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fill="transparent" d="M0 0h900v600H0z" />
                <path
                  d="M234.689 338.305c-2.352 4.197-7.503 4.774-15.756 3.977-6.206-.602-11.866-1.067-18.073-4.451-4.344-2.367-7.783-5.569-10.293-8.679-2.72-3.37-6.517-7.215-4.656-11.036 2.558-5.248 17.348-9.668 31.71-2.44 15.777 7.943 19.369 18.526 17.068 22.629z"
                  fill="url(#a)"
                />
                <path
                  d="M706.271 310.617c-9.617 7.172-24.878 2.987-24.878 2.987s.339-15.812 9.962-22.978c9.617-7.172 24.871-2.993 24.871-2.993s-.339 15.812-9.955 22.984z"
                  fill="url(#b)"
                />
                <path
                  d="M216.121 174.364c9.141 10.58 27.562 9.653 27.562 9.653s3.589-18.08-5.562-28.655c-9.141-10.58-27.553-9.659-27.553-9.659s-3.588 18.081 5.553 28.661z"
                  fill="url(#c)"
                />
                <circle
                  r="7.888"
                  transform="matrix(-1 0 0 1 439.888 502.888)"
                  fill="#E1E4E5"
                />
                <circle
                  r="8.671"
                  transform="matrix(-1 0 0 1 508.671 437.671)"
                  fill="#E1E4E5"
                />
                <circle
                  r="7"
                  transform="matrix(-1 0 0 1 679 173.641)"
                  fill="#E1E4E5"
                />
                <circle
                  r="8.657"
                  transform="scale(1 -1) rotate(-75 -46.733 -225.94)"
                  fill="#E1E4E5"
                />
                <circle
                  r="11.517"
                  transform="matrix(-1 0 0 1 321.517 124.517)"
                  fill="#E1E4E5"
                />
                <ellipse
                  rx="8.859"
                  ry="7.087"
                  transform="matrix(-1 0 0 1 744.455 402.605)"
                  fill="#E1E4E5"
                />
                <circle
                  r="12.725"
                  transform="matrix(0 1 1 0 220.725 471.725)"
                  fill="#E1E4E5"
                />
                <path
                  d="M659.522 391.983h.232c1.372 19.445 15.833 19.745 15.833 19.745s-15.946.311-15.946 22.779c0-22.468-15.946-22.779-15.946-22.779s14.455-.3 15.827-19.745z"
                  fill="#E1E4E5"
                />
                <path
                  d="M309.552 475.379h300.517c23.71 0 42.931-19.221 42.931-42.931V131.931C653 108.221 633.779 89 610.069 89H395.414c-23.71 0-42.931 19.221-42.931 42.931v300.517c0 23.71-19.221 42.931-42.931 42.931v0c-23.711 0-42.931-19.221-42.931-42.931v-236.12c0-23.711 19.22-42.931 42.931-42.931h42.931m75.13 21.466h150.259m-150.259 85.862h150.259m-150.259 85.864h150.259"
                  stroke="#E1E4E5"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M271.673 311.543c0 49.798 40.366 90.164 90.164 90.164 49.797 0 90.163-40.366 90.163-90.164 0-49.797-40.366-90.164-90.163-90.164-49.798 0-90.164 40.367-90.164 90.164z"
                  fill="#fff"
                  stroke="#e2e8f0"
                  strokeWidth="20.082"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="m252 421.379 38.525-38.525"
                  stroke="#e2e8f0"
                  strokeWidth="20.082"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M541 420.708v-41.569a39.288 39.288 0 0 1 19.519-33.948l39.281-22.862a39.298 39.298 0 0 1 39.518 0l39.281 22.862a39.279 39.279 0 0 1 19.527 33.948v41.569a39.29 39.29 0 0 1-19.518 33.948l-39.282 22.862a39.296 39.296 0 0 1-39.517 0l-39.282-22.862A39.279 39.279 0 0 1 541 420.708z"
                  fill="#e2e8f0"
                />
                <path
                  d="M619.563 399.924v-39.282m-.009 69.834a2.17 2.17 0 0 0-2.173 2.183 2.183 2.183 0 1 0 2.173-2.183"
                  stroke="#fff"
                  strokeWidth="13.094"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="a"
                    x1="218.844"
                    y1="365.509"
                    x2="197.88"
                    y2="271.306"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#EEE" />
                  </linearGradient>
                  <linearGradient
                    id="b"
                    x1="668.654"
                    y1="334.377"
                    x2="743.28"
                    y2="249.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#EEE" />
                  </linearGradient>
                  <linearGradient
                    id="c"
                    x1="252.959"
                    y1="210.862"
                    x2="189.364"
                    y2="95.507"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#EEE" />
                  </linearGradient>
                </defs>
              </svg>
            }
          />
        )}
      </div>
    </div>
  );
};

ReservationsArea.propTypes = {
  //
};

type CustomerReservationsItemProps = {
  reservation: CustomerBooking;
  showUpdate: boolean;
  showPay: boolean;
};

const CustomerReservationsItem: React.FC<CustomerReservationsItemProps> = ({
  reservation,
  showUpdate,
  showPay,
}) => {
  const getOccupantWithPaymentDue = useCallback((): Occupant[] | null => {
    let occupants: Occupant[] = [];
    reservation.stays?.forEach((x) => {
      x?.occupants?.forEach((y) => {
        if (y?.paymentDues && y.paymentDues.length > 0) {
          occupants = [...occupants, y] as Occupant[];
        }
      });
    });
    return occupants.length > 0 ? occupants : null;
  }, [reservation]);

  const getRemainingCustomPayment = (): number => {
    let remaining = 0;
    getOccupantWithPaymentDue()?.map((o) => {
      o?.paymentDues?.map((p) => {
        remaining += p?.remainingAmount?.amount;
      });
    });

    if (remaining === 0) {
      remaining = reservation.totalAmount?.amount;
    }

    return remaining;
  };

  /* Hooks */
  const {
    cart,
    setCart,
    updateCartPaymentMethod,
    clear,
    updatePayerPaymentsDue,
  } = useCart();
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue, authMode } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const [searchParams] = useSearchParams();

  /* Custom Amount */
  const [customAmount, setCustomAmount] = useState<number>(
    getRemainingCustomPayment()
  );
  const [customAmountError, setCustomAmountError] = useState<boolean>(false);

  /* Payment Method / Option State [Custom Amount] */
  const [selectedCustomPaymentMethod, setSelectedCustomPaymentMethod] =
    useState<string>();
  const [selectedCustomPaymentOption, setSelectedCustomPaymentOption] =
    useState<string>();
  const [isRedirectCustomPSP, setIsRedirectCustomPSP] =
    useState<boolean>(false);

  /* Payment Method / Option State */
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>();

  useEffect(() => {
    if (selectedPaymentMethod) {
      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        selectedPaymentOption ? parseInt(selectedPaymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );
    }
  }, [selectedPaymentMethod]);

  const [selectedPaymentOption, setSelectedPaymentOption] = useState<string>();

  useEffect(() => {
    if (selectedPaymentOption) {
      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        selectedPaymentOption ? parseInt(selectedPaymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );
    }
  }, [selectedPaymentOption]);

  const [isRedirectPSP, setIsRedirectPSP] = useState<boolean>(false);

  /* Actions State*/
  const [loadingFastChecking, setLoadingFastChecking] =
    useState<boolean>(false);
  const [loadingFastCheckout, setLoadingFastCheckout] =
    useState<boolean>(false);
  const [isUpdatingStay, setIsUpdatingStay] = useState<boolean>(false);
  const [loadingPayStay, setLoadingPayStay] = useState<boolean>(false);

  const enableFCI =
    (process.env.REACT_APP_ENABLEFCI ?? "false") === JSON.stringify(true);

  const isCurrentlyInFCI =
    cart?.reservations?.[0]?.id === reservation.id &&
    cart?.fromFastCheckinCheckout === true;

  const {
    data: dataPlans,
    error,
    loading: loadingPlans,
    refetch,
  } = useQuery<Query, QueryGetPaymentPlansArgs>(getPaymentPlansQuery(), {
    variables: { session: { name: sessionId!! }, input: {} },
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      //On n'impacte que le B2B
      if (
        authMode === 2 &&
        (data?.getPaymentPlans?.paymentPlans?.length ?? 0) === 1 &&
        data?.getPaymentPlans?.paymentPlans?.[0]?.code
      ) {
        //On a un seul paymentPlan, on le set directement si rien au panier
        if (cart && !cart?.reservations?.[0]?.selectedPaymentDue) {
          updatePayerPaymentsDue(
            data?.getPaymentPlans?.paymentPlans?.[0]?.code,
            (err) => {}
          );
        }
      }
    },
  });

  const handleUpdate = async () => {
    setIsUpdatingStay(true);
    try {
      const { data: dataReset } = await client.mutate<
        Mutation,
        MutationResetCartArgs
      >({
        mutation: resetCartMutation(),
        variables: {
          session: { name: sessionId },
        },
        fetchPolicy: "no-cache",
      });

      if (dataReset && dataReset.resetCart && dataReset.resetCart.cart) {
        const { data: dataUnlock, errors: errorsUnlock } = await client.mutate<
          Mutation,
          MutationUnlockBookingArgs
        >({
          mutation: unlockBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservation.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        const { data: dataUpdateCustomerBooking } = await client.mutate<
          Mutation,
          MutationUpdateCustomerBookingArgs
        >({
          mutation: updateCustomerBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservation.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        if (
          dataUpdateCustomerBooking &&
          dataUpdateCustomerBooking?.updateCustomerBooking &&
          dataUpdateCustomerBooking?.updateCustomerBooking?.cart
        ) {
          const webuser = getWebUser();
          if (webuser === "webEN" || webuser === "web_en_b2b") {
            window.location.href = `${
              process.env.REACT_APP_CUSTOMER_URL_EN ?? window.location.origin
            }/reservation?pi=false&webuser=${webuser}&session=${sessionId}&edit=1`;
          } else {
            window.location.href = `${
              process.env.REACT_APP_CUSTOMER_URL ?? window.location.origin
            }/reservation?pi=false&webuser=${webuser}&session=${sessionId}&edit=1`;
          }
        }
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        toast.error(error.message);
      }
    } finally {
      setIsUpdatingStay(false);
    }
  };

  const handleSubmitCustomPayment = (openPopup: () => void) => {
    if (
      customAmount <=
        parseFloat(reservation.totalAmount?.amount?.toFixed(2) ?? "0") &&
      customAmount > 0
    ) {
      setCustomAmountError(false);
      openPopup();
    } else {
      toast.error(getLanguageVarValue("LANG_neobooking_invalid_amount_input"));
      setCustomAmountError(true);
      setCustomAmount(
        parseFloat(reservation.totalAmount?.amount?.toFixed(2) ?? "0")
      );
    }
  };

  const handleRedirectPSP = (paymentdueId: number) => {
    if (sessionId) {
      setIsRedirectPSP(true);

      const reservationUrl = window.location.origin + "/mon-compte";
      const confirmationUrl = window.location.origin + "/confirmation";

      const acceptUrl = `${confirmationUrl}?s=s&r=${reservation.id}&ca=true`;
      const declineUrl = `${reservationUrl}?s=d&session=${sessionId}&r=${reservation.id}`;
      const cancelUrl = `${reservationUrl}?s=c&session=${sessionId}&r=${reservation.id}`;
      const exceptionUrl = `${reservationUrl}?s=e&session=${sessionId}&r=${reservation.id}`;

      const paymentGatewayForInstalmentInput: PaymentGatewayForInstalmentInput =
        {
          instalmentId: paymentdueId,
          input: {
            bookingStatus: BookingStatus.Guaranteed,
            ccardType: parseInt(selectedPaymentOption ?? "1"),
            paymentMode: "CB",
            paymentOptions: "CB",
            automaticPayment: true,
            url: {
              acceptUrl,
              declineUrl,
              cancelUrl,
              exceptionUrl,
            },
          },
        };

      client
        .mutate<Mutation, MutationRedirectPaymentGatewayForInstalmentArgs>({
          mutation: redirectPaymentGatewayForInstalmentMutation(),
          variables: {
            session: { name: sessionId!! },
            input: paymentGatewayForInstalmentInput,
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.redirectPaymentGatewayForInstalment?.gateway) {
            setIsRedirectPSP(false);
            window.location.href =
              response.data.redirectPaymentGatewayForInstalment?.gateway;
          }
        })
        .catch((e: GraphQLError) => {
          toast.error(e.message);
          console.log(e);
        })
        .finally(() => {
          setIsRedirectPSP(false);
        });
    }
  };

  const handleRedirectCustomPSP = () => {
    if (sessionId) {
      setIsRedirectCustomPSP(true);
      const reservationUrl = window.location.origin + "/reservation";
      const confirmationUrl = window.location.origin + "/confirmation";

      const acceptUrl = `${confirmationUrl}?s=s&r=${reservation.id}`;
      const declineUrl = `${reservationUrl}?s=d&r=${reservation.id}`;
      const cancelUrl = `${reservationUrl}?s=c&r=${reservation.id}`;
      const exceptionUrl = `${reservationUrl}?s=e&r=${reservation.id}`;

      const instalmentId =
        reservation?.stays?.[0]?.occupants?.[0]?.paymentDues?.[0]?.id ?? 0;

      const paymentGatewayForAmountInput: PaymentGatewayForAmountInput = {
        bookingId: reservation.id!!,
        ccAmount: customAmount,
        instalmentId: instalmentId,
        input: {
          bookingStatus: BookingStatus.Guaranteed,
          ccardType: parseInt(selectedCustomPaymentOption ?? "1"),
          paymentMode: "CB",
          paymentOptions: "CB",
          automaticPayment: true,
          url: {
            acceptUrl,
            declineUrl,
            cancelUrl,
            exceptionUrl,
          },
        },
      };

      client
        .mutate<Mutation, MutationRedirectPaymentGatewayForAmountArgs>({
          mutation: redirectPaymentGatewayForAmountMutation(),
          variables: {
            session: { name: sessionId!! },
            input: paymentGatewayForAmountInput,
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data?.redirectPaymentGatewayForAmount?.gateway) {
            window.location.href =
              response.data.redirectPaymentGatewayForAmount?.gateway;
          }
        })
        .catch((err: GraphQLError) => {
          toast.error(err.message);
          console.log(err);
        })
        .finally(() => {
          setIsRedirectCustomPSP(false);
        });
    }
  };
  //#endregion Actions

  const handleAddResaToCart = async (onClick: () => void) => {
    setLoadingPayStay(true);
    try {
      const { data: dataReset } = await client.mutate<
        Mutation,
        MutationResetCartArgs
      >({
        mutation: resetCartMutation(),
        variables: {
          session: { name: sessionId },
        },
        fetchPolicy: "no-cache",
      });

      if (dataReset && dataReset.resetCart && dataReset.resetCart.cart) {
        const { data: dataUnlock, errors: errorsUnlock } = await client.mutate<
          Mutation,
          MutationUnlockBookingArgs
        >({
          mutation: unlockBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservation.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        const { data: dataUpdateCustomerBooking } = await client.mutate<
          Mutation,
          MutationUpdateCustomerBookingArgs
        >({
          mutation: updateCustomerBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservation.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        if (
          dataUpdateCustomerBooking &&
          dataUpdateCustomerBooking?.updateCustomerBooking &&
          dataUpdateCustomerBooking?.updateCustomerBooking?.cart
        ) {
          setCart(dataUpdateCustomerBooking?.updateCustomerBooking?.cart);
          onClick();
          if (authMode === 2) {
            await refetch();
          }
        }
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        toast.error(error.message);
      }
    } finally {
      setLoadingPayStay(false);
    }
  };

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handleDoFastCheckin = async () => {
    if (sessionId && reservation.id) {
      setLoadingFastChecking(true);
      const bookingId = reservation.id;

      const webuser = getWebUser();
      let baseUrl =
        webuser === "webEN" || webuser === "web_en_b2b"
          ? process.env.REACT_APP_CUSTOMER_URL_EN ?? window.location.origin
          : process.env.REACT_APP_CUSTOMER_URL ?? window.location.origin;

      if (isCurrentlyInFCI) {
        window.location.href = `${baseUrl}/reservation/pre-checkin?session=${sessionId}&webuser=${webuser}`;
      }

      try {
        //On reset le cart
        const { data: dataReset, errors: errorsReset } = await client.mutate<
          Mutation,
          MutationResetCartArgs
        >({
          mutation: resetCartMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        });
        if (errorsReset && errorsReset.length > 0) {
          setLoadingFastChecking(false);
        } else if (dataReset && dataReset.resetCart) {
          //Le cart est bien reset, on unlock la resa
          const { data: dataUnlock, errors: errorsUnlock } =
            await client.mutate<Mutation, MutationUnlockBookingArgs>({
              mutation: unlockBookingMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  bookingId: bookingId,
                },
              },
              fetchPolicy: "no-cache",
            });

          if (errorsUnlock && errorsUnlock.length > 0) {
            setLoadingFastChecking(false);
          } else if (
            dataUnlock &&
            dataUnlock.unlockBooking &&
            dataUnlock.unlockBooking.bookingUnlocked
          ) {
            //On a unlock la resa, on peux doFastcheckin
            const { data: dataFCI, errors: errorsFCI } = await client.mutate<
              Mutation,
              MutationDoFastCheckinArgs
            >({
              mutation: doFastCheckinLightMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  bookingId: bookingId,
                },
              },
              fetchPolicy: "no-cache",
            });

            if (errorsFCI && errorsFCI.length > 0) {
              setLoadingFastChecking(false);
            } else if (
              dataFCI &&
              dataFCI.doFastCheckin &&
              dataFCI.doFastCheckin.reservation
            ) {
              //On a dofastcheckin, on add la resa au panier
              setLoadingFastChecking(false);
              window.location.href = `${baseUrl}/reservation/pre-checkin?session=${sessionId}&webuser=${webuser}`;
            }
          }
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          toast.error(error.message);
        }
      } finally {
        setLoadingFastChecking(false);
      }
    }
  };

  const handleDoFastCheckout = () => {
    if (sessionId) {
      setLoadingFastCheckout(true);
      client
        .mutate<Mutation, MutationDoFastCheckoutArgs>({
          mutation: doFastCheckoutMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: reservation.id,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (
            response.data &&
            response.data.doFastCheckout &&
            response.data.doFastCheckout.reservation
          ) {
            setLoadingFastCheckout(false);

            const webuser = getWebUser();
            let baseUrl =
              webuser === "webEN" || webuser === "web_en_b2b"
                ? process.env.REACT_APP_CUSTOMER_URL_EN ??
                  window.location.origin
                : process.env.REACT_APP_CUSTOMER_URL ?? window.location.origin;

            window.location.href = `${baseUrl}/reservation?session=${sessionId}&webuser=${webuser}`;
          }
        })
        .catch((err) => {
          toast.error(err.message);
          setLoadingFastCheckout(false);
        });
    }
  };

  return (
    <div className="tw-text-sm tw-rounded-[26px] tw-border tw-border-[#707070] tw-grid tw-grid-cols-12">
      <div className="tw-col-span-12 sm:tw-col-span-4">
        <img
          className="tw-object-cover tw-max-h-[150px] tw-rounded-[26px] sm:tw-h-full tw-w-full sm:tw-max-h-full"
          src={reservation?.property?.imagePath ?? ""}
          onError={replaceImgOnError}
        ></img>
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-8 tw-relative">
        <div className="tw-p-3 tw-w-full tw-flex tw-flex-col tw-justify-between tw-h-full">
          <div>
            <div>
              <span className="tw-font-semibold">
                {reservation.property?.address?.city}
              </span>{" "}
              - {reservation.property?.label}
            </div>
            <div className="tw-text-sm">
              <div className="tw-lowercase tw-mb-1">
                {reservation.nbDays}{" "}
                {getLanguageVarValue("LANG_neobooking_nights")}{" "}
                {getLanguageVarValue("LANG_neobooking_from_date")}{" "}
                {dateFormat(new Date(reservation.startDate), "dd/mm/yyyy")}{" "}
                {getLanguageVarValue("LANG_neobooking_to_date")}{" "}
                {dateFormat(new Date(reservation.endDate), "dd/mm/yyyy")}{" "}
              </div>
              <div>{getReservationRoomsInfosName(reservation)}</div>
              {authMode === 2 ? (
                <div>
                  {reservation.primaryOccupant?.lastName}{" "}
                  {reservation.primaryOccupant?.firstName}
                </div>
              ) : (
                <></>
              )}
              <div>
                {reservation.status?.label} - n°{reservation.id}
              </div>
            </div>
          </div>
          <div className="tw-flex lg:tw-hidden tw-gap-2 tw-justify-end tw-my-2">
            {reservation.isEnableFastCheckin && enableFCI && authMode !== 2 ? (
              <Button
                onClick={handleDoFastCheckin}
                type="button"
                isLoading={loadingFastChecking}
                disabled={loadingFastChecking}
                className={classNames(
                  mode === "vacanceole"
                    ? "tw-bg-v_red-dark"
                    : "tw-bg-primary_corseole-500",
                  "tw-text-white tw-text-sm lg:tw-text-base tw-font-medium tw-py-1 tw-px-2 tw-rounded-full"
                )}
                loadingChildren={
                  <>
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                      {getLanguageVarValue(
                        isCurrentlyInFCI
                          ? "LANG_neobooking_area_res_continue_fastcheckin"
                          : "LANG_neobooking_area_res_do_fastcheckin"
                      )}
                      <LoadingDots />
                    </div>
                  </>
                }
              >
                <div className="tw-flex tw-items-center tw-gap-x-2">
                  {getLanguageVarValue(
                    isCurrentlyInFCI
                      ? "LANG_neobooking_area_res_continue_fastcheckin"
                      : "LANG_neobooking_area_res_do_fastcheckin"
                  )}
                  <ArrowCircleRight />
                </div>
              </Button>
            ) : (
              <></>
            )}

            <div className="tw-flex tw-justify-end">
              <div
                className={`${
                  mode === "vacanceole"
                    ? "tw-bg-v_blue-dark"
                    : "tw-bg-primary_corseole-500"
                } tw-text-white tw-font-bold tw-text-xl tw-py-1 tw-px-2 tw-rounded-md`}
              >
                {reservation.totalAmount?.amount}
                {getCurrencySymbol(reservation.totalAmount?.currencyCode!!)}
              </div>
            </div>
          </div>
          <div>
            <ul className="tw-flex tw-flex-wrap tw-gap-y-2 tw-gap-x-4 tw-mt-4">
              {showUpdate && !isCurrentlyInFCI && (
                <li>
                  <Button
                    disabled={!reservation.isEnableEditReservation}
                    onClick={
                      reservation.isEnableEditReservation
                        ? handleUpdate
                        : undefined
                    }
                    isLoading={isUpdatingStay}
                    loadingChildren={
                      <>{getLanguageVarValue("LANG_neobooking_editing")}...</>
                    }
                    className="disabled:tw-opacity-50 hover:tw-font-semibold tw-rounded-full tw-border tw-border-v_blue-dark tw-text-v_blue-dark tw-px-2 tw-py-1 tw-font-montserrat"
                  >
                    {getLanguageVarValue("LANG_neobooking_edit")}
                  </Button>
                </li>
              )}
              {reservation.isEnableFastCheckout && enableFCI && authMode !== 2 && (
                <li>
                  <Button
                    onClick={handleDoFastCheckout}
                    type="button"
                    isLoading={loadingFastCheckout}
                    className="disabled:tw-opacity-50 hover:tw-font-semibold"
                    loadingChildren={
                      <>
                        {getLanguageVarValue(
                          "LANG_neobooking_area_res_doing_fastcheckout"
                        )}
                      </>
                    }
                  >
                    {getLanguageVarValue(
                      "LANG_neobooking_area_res_do_fastcheckout"
                    )}
                  </Button>
                </li>
              )}
              {showPay &&
                (authMode !== 2
                  ? !isReservationPaid(reservation)
                  : true) /* SI authmode 2, on affiche toujours le bouton car on sais toujours pas si c'est payé tant qu'on est pas dans la resa */ && (
                  <li>
                    <Popup
                      title={getLanguageVarValue(
                        authMode === 2
                          ? "LANG_neobooking_espace_b2b_view_plans"
                          : "LANG_neobooking_area_res_payment_pay_amount"
                      )}
                      size="lg"
                      onClose={() => clear((err) => {})}
                      btnElement={(onClick) => (
                        <Button
                          type="button"
                          isLoading={loadingPayStay}
                          disabled={loadingPayStay}
                          loadingChildren={
                            <>
                              {getLanguageVarValue(
                                authMode === 2
                                  ? "LANG_neobooking_espace_b2b_view_plans"
                                  : "LANG_neobooking_area_res_pay_stay"
                              )}
                              <LoadingDots />
                            </>
                          }
                          onClick={() => handleAddResaToCart(onClick)}
                          className="disabled:tw-opacity-50 hover:tw-font-semibold tw-rounded-full tw-border tw-border-v_blue-dark tw-text-v_blue-dark tw-px-2 tw-py-1 tw-font-montserrat"
                        >
                          {getLanguageVarValue(
                            authMode === 2
                              ? "LANG_neobooking_espace_b2b_view_plans"
                              : "LANG_neobooking_area_res_pay_stay"
                          )}
                        </Button>
                      )}
                    >
                      {authMode !== 2 ? (
                        <>
                          <div>
                            <div className="tw-overflow-x-auto">
                              <div className="tw-overflow-hidden"></div>
                              <table className="tw-w-full tw-text-left tw-text-sm">
                                <thead className="tw-bg-gray-50 tw-tracking-wider">
                                  <tr>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_due_by"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_due_date"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_to_pay"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_amount_paid"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_due_date_pay"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_amount_due"
                                      )}
                                    </th>
                                    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-sticky tw--right-1 tw-bg-gray-50">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_action"
                                      )}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="tw-bg-white">
                                  {getOccupantWithPaymentDue()?.map(
                                    (occupant, index) => {
                                      return occupant?.paymentDues?.map(
                                        (paymentDue) => {
                                          return (
                                            <tr
                                              key={`tr_payment_${occupant.guiid}_${index}_${paymentDue?.id}`}
                                            >
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {occupant.firstName}
                                                {` `}
                                                {occupant.lastName}
                                              </td>
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {dateFormat(
                                                  new Date(paymentDue?.date),
                                                  "dd/mm/yyyy"
                                                )}
                                              </td>
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {paymentDue?.amount?.amount?.toFixed(
                                                  2
                                                )}
                                                {getCurrencySymbol(
                                                  paymentDue?.amount
                                                    ?.currencyCode!!
                                                )}
                                              </td>
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {paymentDue?.payments &&
                                                paymentDue?.payments.length >
                                                  0 ? (
                                                  <>
                                                    {paymentDue?.receivedAmount?.amount?.toFixed(
                                                      2
                                                    )}
                                                    {getCurrencySymbol(
                                                      paymentDue?.receivedAmount
                                                        ?.currencyCode ?? ""
                                                    )}
                                                  </>
                                                ) : (
                                                  <></>
                                                )}
                                              </td>
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {paymentDue?.payments &&
                                                paymentDue?.payments.length >
                                                  0 ? (
                                                  <>
                                                    {paymentDue?.payments
                                                      .map((x) =>
                                                        dateFormat(
                                                          x?.date,
                                                          "dd/mm/yyyy"
                                                        )
                                                      )
                                                      .join(" - ")}
                                                  </>
                                                ) : (
                                                  <></>
                                                )}
                                              </td>
                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                {paymentDue?.remainingAmount?.amount?.toFixed(
                                                  2
                                                )}
                                                {getCurrencySymbol(
                                                  paymentDue?.remainingAmount
                                                    ?.currencyCode!!
                                                )}
                                              </td>

                                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-sticky tw--right-1 tw-bg-white">
                                                <Popup
                                                  title={getLanguageVarValue(
                                                    "LANG_neobooking_area_res_payment_due_popup"
                                                  )}
                                                  btnElement={(onClick) => (
                                                    <Button
                                                      shape="link"
                                                      disabled={
                                                        paymentDue
                                                          ?.remainingAmount
                                                          ?.amount <= 0
                                                      }
                                                      size="sm"
                                                      onClick={
                                                        paymentDue
                                                          ?.remainingAmount
                                                          ?.amount <= 0
                                                          ? undefined
                                                          : onClick
                                                      }
                                                    >
                                                      {getLanguageVarValue(
                                                        "LANG_neobooking_area_res_payment_pay"
                                                      )}
                                                    </Button>
                                                  )}
                                                >
                                                  <div className="tw-flex tw-flex-col tw-gap-4">
                                                    <SelectPaymentMethods
                                                      hideANCV={authMode === 2}
                                                      automaticPaymentOnly={
                                                        true
                                                      }
                                                      selected={
                                                        selectedPaymentMethod
                                                      }
                                                      setSelected={
                                                        setSelectedPaymentMethod
                                                      }
                                                      selectedPaymentOption={
                                                        selectedPaymentOption
                                                      }
                                                      setSelectedPaymentOption={
                                                        setSelectedPaymentOption
                                                      }
                                                    />
                                                    <div>
                                                      <Button
                                                        loadingChildren={
                                                          <>
                                                            {getLanguageVarValue(
                                                              "LANG_neobooking_area_res_payment_redirect_in_progress"
                                                            )}
                                                          </>
                                                        }
                                                        isLoading={
                                                          isRedirectPSP
                                                        }
                                                        onClick={() =>
                                                          handleRedirectPSP(
                                                            paymentDue?.id!!
                                                          )
                                                        }
                                                      >
                                                        {getLanguageVarValue(
                                                          "LANG_neobooking_area_res_payment_pay"
                                                        )}{" "}
                                                        <span>
                                                          {paymentDue?.amount?.amount?.toFixed(
                                                            2
                                                          )}
                                                          {getCurrencySymbol(
                                                            paymentDue?.amount
                                                              ?.currencyCode!!
                                                          )}
                                                        </span>
                                                      </Button>
                                                    </div>
                                                  </div>
                                                </Popup>
                                              </td>
                                            </tr>
                                          );
                                        }
                                      );
                                    }
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {(reservation.stays?.[0]?.occupants?.[0]
                            ?.paymentDues?.[0]?.receivedAmount?.amount ?? -1) >
                            0 &&
                            (reservation.stays?.[0]?.occupants?.[0]
                              ?.paymentDues?.[0]?.payments?.length ?? -1) >
                              0 && (
                              <>
                                <div className="tw-relative tw-py-3">
                                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center">
                                    <div className="tw-w-full tw-border-t tw-border-gray-300" />
                                  </div>
                                  <div className="tw-relative tw-flex tw-justify-center tw-text-sm">
                                    <span className="tw-px-2 tw-bg-white tw-text-gray-500">
                                      {getLanguageVarValue(
                                        "LANG_neobooking_area_res_payment_pay_custom"
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div className="tw-flex">
                                  <NumericInput
                                    precision={2}
                                    format={(value) => {
                                      return (
                                        value +
                                        getCurrencySymbol(
                                          reservation.totalAmount
                                            ?.currencyCode!!
                                        )
                                      );
                                    }}
                                    noStyle
                                    className="tw-rounded tw-border focus:tw-border-gray-400 tw-border-gray-300 tw-py-1 tw-px-3 tw-leading-8 tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-rounded-tr-none tw-rounded-br-none tw-flex-1 tw-w-full"
                                    step={0.01}
                                    value={customAmount}
                                    max={getRemainingCustomPayment()}
                                    onChange={(e) => setCustomAmount(e ? e : 0)}
                                  />
                                  <Popup
                                    title={getLanguageVarValue(
                                      "LANG_neobooking_area_res_payment_pay_amount"
                                    )}
                                    size="lg"
                                    btnElement={(onClick) => (
                                      <Button
                                        type="button"
                                        onClick={() =>
                                          handleSubmitCustomPayment(onClick)
                                        }
                                        additionalClassname="tw-rounded-tl-none tw-rounded-bl-none bg-red-500"
                                      >
                                        {getLanguageVarValue(
                                          "LANG_neobooking_area_res_payment_pay"
                                        )}
                                      </Button>
                                    )}
                                    btnCloseText={getLanguageVarValue(
                                      "LANG_neobooking_area_res_cancel"
                                    )}
                                  >
                                    <div className="tw-flex tw-flex-col tw-gap-4">
                                      <SelectPaymentMethods
                                        automaticPaymentOnly={true}
                                        hideANCV={authMode === 2}
                                        selected={selectedCustomPaymentMethod}
                                        setSelected={
                                          setSelectedCustomPaymentMethod
                                        }
                                        selectedPaymentOption={
                                          selectedCustomPaymentOption
                                        }
                                        setSelectedPaymentOption={
                                          setSelectedCustomPaymentOption
                                        }
                                      />
                                      <div>
                                        <Button
                                          loadingChildren={
                                            <>
                                              {getLanguageVarValue(
                                                "LANG_neobooking_area_res_payment_redirect_in_progress"
                                              )}
                                            </>
                                          }
                                          isLoading={isRedirectCustomPSP}
                                          onClick={handleRedirectCustomPSP}
                                        >
                                          {getLanguageVarValue(
                                            "LANG_neobooking_area_res_payment_pay"
                                          )}{" "}
                                          <span>
                                            {customAmount.toFixed(2)}
                                            {getCurrencySymbol(
                                              reservation.totalAmount
                                                ?.currencyCode!!
                                            )}
                                          </span>
                                        </Button>
                                      </div>
                                    </div>
                                  </Popup>
                                </div>
                              </>
                            )}
                          {customAmountError && customAmountError && (
                            <div className="tw-text-sm tw-text-red-500 tw-italic">
                              {getLanguageVarValue(
                                "LANG_neobooking_area_res_payment_amount_error"
                              )}{" "}
                              {reservation.totalAmount?.amount?.toFixed(2)}
                              {getCurrencySymbol(
                                reservation.totalAmount?.currencyCode!!
                              )}
                            </div>
                          )}
                        </>
                      ) : loadingPlans ? (
                        <LoadingSpinner />
                      ) : (
                        <>
                          {(dataPlans?.getPaymentPlans?.paymentPlans?.length ??
                            0) > 0 &&
                          (dataPlans?.getPaymentPlans?.paymentPlans?.[0]
                            ?.partnerPaymentDues?.length ?? 0) > 0 ? (
                            <>
                              <div className="tw-overflow-x-auto tw-mb-4">
                                <div className="tw-overflow-hidden"></div>
                                <table className="tw-w-full tw-text-left tw-text-sm">
                                  <thead className="tw-bg-gray-50 tw-tracking-wider">
                                    <tr>
                                      <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                        {getLanguageVarValue(
                                          "LANG_neobooking_area_res_payment"
                                        )}
                                      </th>
                                      <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                        {getLanguageVarValue(
                                          "LANG_neobooking_payment_due_date"
                                        )}
                                      </th>
                                      <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                                        {getLanguageVarValue(
                                          "LANG_neobooking_payment_due_amount"
                                        )}
                                      </th>
                                      <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-sticky tw--right-1 tw-bg-gray-50">
                                        {getLanguageVarValue(
                                          "LANG_neobooking_area_res_payment_action"
                                        )}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="tw-bg-white">
                                    {dataPlans?.getPaymentPlans?.paymentPlans?.map(
                                      (plan, planIndex) => {
                                        return plan?.partnerPaymentDues?.map(
                                          (
                                            partnerPaymentDue,
                                            partnerPaymentIndex
                                          ) => {
                                            return (
                                              <tr
                                                key={`tr_payment_${plan?.code}_${planIndex}_${partnerPaymentDue?.amount?.amount}_${partnerPaymentIndex}`}
                                              >
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                  {partnerPaymentIndex + 1}
                                                  {partnerPaymentIndex + 1 === 1
                                                    ? getLanguageVarValue(
                                                        "LANG_neobooking_area_res_payment_er"
                                                      )
                                                    : getLanguageVarValue(
                                                        "LANG_neobooking_area_res_payment_eme"
                                                      )}{" "}
                                                  <span className="tw-lowercase">
                                                    {getLanguageVarValue(
                                                      "LANG_neobooking_acompte"
                                                    )}
                                                  </span>
                                                </td>
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                  {dateFormat(
                                                    new Date(
                                                      partnerPaymentDue?.date
                                                    ),
                                                    "dd/mm/yyyy"
                                                  )}
                                                </td>
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                  {partnerPaymentDue?.amount?.amount?.toFixed(
                                                    2
                                                  )}
                                                  {getCurrencySymbol(
                                                    partnerPaymentDue?.amount
                                                      ?.currencyCode!!
                                                  )}
                                                </td>
                                                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-sticky tw--right-1 tw-bg-white">
                                                  <Popup
                                                    title={getLanguageVarValue(
                                                      "LANG_neobooking_area_res_payment_due_popup"
                                                    )}
                                                    btnElement={(onClick) => (
                                                      <Button
                                                        shape="link"
                                                        disabled={
                                                          partnerPaymentDue
                                                            ?.remainingAmount
                                                            ?.amount <= 0
                                                        }
                                                        size="sm"
                                                        onClick={
                                                          partnerPaymentDue
                                                            ?.remainingAmount
                                                            ?.amount <= 0
                                                            ? undefined
                                                            : onClick
                                                        }
                                                      >
                                                        {getLanguageVarValue(
                                                          "LANG_neobooking_area_res_payment_pay"
                                                        )}
                                                      </Button>
                                                    )}
                                                  >
                                                    <div className="tw-flex tw-flex-col tw-gap-4">
                                                      <SelectPaymentMethods
                                                        hideANCV={
                                                          authMode === 2
                                                        }
                                                        automaticPaymentOnly={
                                                          true
                                                        }
                                                        selected={
                                                          selectedPaymentMethod
                                                        }
                                                        setSelected={
                                                          setSelectedPaymentMethod
                                                        }
                                                        selectedPaymentOption={
                                                          selectedPaymentOption
                                                        }
                                                        setSelectedPaymentOption={
                                                          setSelectedPaymentOption
                                                        }
                                                      />
                                                      <div>
                                                        <Button
                                                          loadingChildren={
                                                            <>
                                                              {getLanguageVarValue(
                                                                "LANG_neobooking_area_res_payment_redirect_in_progress"
                                                              )}
                                                            </>
                                                          }
                                                          isLoading={
                                                            isRedirectPSP
                                                          }
                                                          onClick={() =>
                                                            handleRedirectPSP(
                                                              partnerPaymentDue?.id ??
                                                                0
                                                            )
                                                          }
                                                        >
                                                          {getLanguageVarValue(
                                                            "LANG_neobooking_area_res_payment_pay"
                                                          )}{" "}
                                                          <span>
                                                            {partnerPaymentDue?.amount?.amount?.toFixed(
                                                              2
                                                            )}
                                                            {getCurrencySymbol(
                                                              partnerPaymentDue
                                                                ?.amount
                                                                ?.currencyCode!!
                                                            )}
                                                          </span>
                                                        </Button>
                                                      </div>
                                                    </div>
                                                  </Popup>
                                                </td>
                                              </tr>
                                            );
                                          }
                                        );
                                      }
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <p className="tw-mt-6 tw-italic">
                                {getLanguageVarValue(
                                  "LANG_neobooking_pay_b2b_echeance"
                                )}
                              </p>
                            </>
                          ) : (
                            <div>
                              {getLanguageVarValue(
                                "LANG_neobooking_espace_b2b_no_plans"
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </Popup>
                  </li>
                )}
              <li>
                <Popup
                  size="lg"
                  btnElement={(onClick) => (
                    <button
                      className="hover:tw-font-semibold tw-rounded-full tw-border tw-border-v_blue-dark tw-text-v_blue-dark tw-px-2 tw-py-1 tw-font-montserrat"
                      onClick={onClick}
                    >
                      {getLanguageVarValue(
                        "LANG_neobooking_area_res_my_documents"
                      )}
                    </button>
                  )}
                  title={getLanguageVarValue(
                    "LANG_neobooking_area_res_my_documents"
                  )}
                >
                  <ReservationDocuments reservationId={reservation.id!!} />
                </Popup>
              </li>
              <li>
                <PropertyPopup
                  property={reservation.property!!}
                  btnElement={(onClick) => (
                    <button
                      className="hover:tw-font-semibold tw-rounded-full tw-border tw-border-v_blue-dark tw-text-v_blue-dark tw-px-2 tw-py-1 tw-font-montserrat"
                      onClick={onClick}
                    >
                      {getLanguageVarValue(
                        "LANG_neobooking_area_res_contact_etab"
                      )}
                    </button>
                  )}
                />
              </li>
            </ul>
          </div>
        </div>
        <div className="tw-hidden lg:tw-flex tw-flex-col tw-gap-2 tw-justify-end tw-absolute tw-right-2 tw-bottom-[50px]">
          <div className="tw-flex tw-justify-end">
            <div
              className={`${
                mode === "vacanceole"
                  ? "tw-bg-v_blue-dark"
                  : "tw-bg-primary_corseole-500"
              } tw-text-white tw-font-bold tw-text-xl tw-py-1 tw-px-2 tw-rounded-md`}
            >
              {reservation.totalAmount?.amount}
              {getCurrencySymbol(reservation.totalAmount?.currencyCode!!)}
            </div>
          </div>
          {reservation.isEnableFastCheckin && enableFCI && authMode !== 2 ? (
            <Button
              onClick={handleDoFastCheckin}
              type="button"
              isLoading={loadingFastChecking}
              disabled={loadingFastChecking}
              className={classNames(
                mode === "vacanceole"
                  ? "tw-bg-v_red-dark"
                  : "tw-bg-primary_corseole-500",
                "tw-text-white tw-text-sm lg:tw-text-base tw-font-medium tw-py-1 tw-px-2 tw-rounded-full"
              )}
              loadingChildren={
                <div className="tw-flex tw-items-center tw-gap-x-2">
                  {getLanguageVarValue(
                    isCurrentlyInFCI
                      ? "LANG_neobooking_area_res_continue_fastcheckin"
                      : "LANG_neobooking_area_res_do_fastcheckin"
                  )}
                  <LoadingDots />
                </div>
              }
            >
              <div className="tw-flex tw-items-center tw-gap-x-2">
                {getLanguageVarValue(
                  isCurrentlyInFCI
                    ? "LANG_neobooking_area_res_continue_fastcheckin"
                    : "LANG_neobooking_area_res_do_fastcheckin"
                )}
                <ArrowCircleRight />
              </div>
            </Button>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationsArea;
